import * as React from 'react'
import { MultiTenantLocationDetails } from '@user/store/map/typings'
import { useData } from '@user/features/MapView/MultiTenantDrawer/MultiTenantDrawerContentSingle/hooks/useData'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import Stack from '@mui/material/Stack'
import { LocationLink } from '@user/features/MapView/LocationLink'
import Link from '@mui/material/Link'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'
import { KnockStatusModal } from '@user/features/MapView/KnockStatusModal'
import { useHooks } from '@user/features/MapView/MultiTenantDrawer/MultiTenantDrawerContentSingle/hooks/useHooks'
import LaunchIcon from '@mui/icons-material/Launch'
import { Theme } from '@mui/material/styles'
import { StatusSection } from '@user/features/MapView/MapBubbleContents/atoms/StatusSection'
import {
  ClearKnockStatusButton,
  ClearKnockStatusConfirmDialog,
  useClearKnockStatus,
  useKnockStatusEditPermission,
} from '@user/features/MapView/ClearKnockStatus'
import { hasViewableHistory } from '@user/utils/knockStatusFormatting'

export type Props = {
  location: MultiTenantLocationDetails
  setDrawerOpen: (open: boolean) => void
  setIsLoading: (open: boolean) => void
  noViewPermissionForAllLocations: boolean
  onRefresh?: () => void
  setShowHistory: (show: boolean) => void
  onViewKnockStatusHistory?: (location: any) => void
  onReviewPendingLead?: (draftId: string) => void
}

function MultiTenantDrawerContentSingle(props: Props) {
  const {
    location,
    setDrawerOpen,
    noViewPermissionForAllLocations,
    setIsLoading,
    onRefresh,
    setShowHistory,
    onViewKnockStatusHistory,
    onReviewPendingLead,
  } = props
  const isD2dEnabled = useD2DKnockEnabled()
  const {
    addressDisplay,
    entityType,
    formattedPhone,
    nameAndCompanyDisplay,
    variant,
    entityId,
    serviceLocationId,
    locationCode,
    externalLinkId,
    hasViewPermission,
    salesFunnelStage,
    openKnockStatusModal,
    setOpenKnockStatusModal,
    knockStatus,
    knockStatusLocationId,
    hasKnockStatus,
  } = useData({ location })

  const [canEdit] = useKnockStatusEditPermission(variant)

  const {
    clearConfirmOpen,
    isClearing,
    openClearConfirm,
    closeClearConfirm,
    handleClearKnockStatus,
  } = useClearKnockStatus({ knockStatusLocationId, variant, onRefresh })

  const {
    onViewMoreClick,
    handleKnockStatusModalOpen,
    handleKnockStatusModalClose,
    handleViewKnockStatusHistoryClick,
    onViewDetails,
    onPhoneNumberClick,
    onAddressMapsClick,
    handleSaveReason,
    isSavingReason,
  } = useHooks({
    serviceLocationId,
    setOpenKnockStatusModal,
    entityId,
    variant,
    setDrawerOpen,
    formattedPhone,
    setIsLoading,
    setShowHistory,
    addressDisplay,
    onReviewPendingLead,
    location,
    onViewKnockStatusHistory,
    knockStatusHistoryId: knockStatus.id,
    knockStatusLocationId: knockStatus.knockStatusLocationId,
  })

  return (
    <Stack
      sx={{
        px: 2.4,
        pt: 0.8,
        pb: 1.1,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Typography variant="caption">{entityType}</Typography>
      <Link
        onClick={onViewDetails}
        sx={{
          cursor: 'pointer',
          mb: 0.4,
        }}
      >
        <Typography
          variant="subtitle3"
          fontSize={'16px'}
          sx={{ color: '#1565C0' }}
        >
          {nameAndCompanyDisplay}
        </Typography>
      </Link>

      {addressDisplay ? (
        <Link
          component="button"
          type="button"
          onClick={onAddressMapsClick}
          sx={{
            cursor: 'pointer',
            textAlign: 'left',
            border: 'none',
            background: 'none',
            padding: 0,
            mt: 0.4,
            display: 'inline-flex',
            alignItems: 'center',
            maxWidth: '100%',
          }}
        >
          <Typography
            variant="body2"
            sx={{
              color: '#1565C0',
              '&:hover': { color: 'rgba(25, 112, 211, 0.9)' },
            }}
          >
            {addressDisplay}
          </Typography>
          <LaunchIcon
            sx={{
              marginLeft: (theme: Theme) => theme.spacing(0.5),
              fontSize: '18px',
              color: '#1565C0',
              flexShrink: 0,
            }}
          />
        </Link>
      ) : null}

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        {formattedPhone ? (
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              fontWeight: '500',
              alignContent: 'center',
            }}
            variant="subtitle4"
          >
            Phone:
            <Button
              color="primary"
              variant="text"
              size="small"
              onClick={onPhoneNumberClick}
              sx={{
                width: 'fit-content',
                color: '#1565C0',
              }}
              disableElevation
            >
              {formattedPhone}
            </Button>
          </Typography>
        ) : null}
        {locationCode && externalLinkId && (
          <LocationLink
            externalLocationId={externalLinkId}
            externalLocationCode={locationCode}
            billingLocation={false}
            typographyVariant="subtitle4"
            fontSize="14px"
            containerSx={{
              alignContent: 'center',
            }}
            linkButtonSx={{
              minWidth: 'fit-content',
              maxWidth: 'fit-content',
              padding: 0.5,
            }}
          />
        )}
      </Stack>

      <StatusSection
        status={salesFunnelStage}
        knockStatus={knockStatus}
        chipMarginTop={Boolean(
          (!locationCode || !externalLinkId) && !formattedPhone,
        )}
        clearButton={
          isD2dEnabled && hasViewPermission && hasKnockStatus && canEdit ? (
            <ClearKnockStatusButton onClick={openClearConfirm} />
          ) : undefined
        }
        viewHistoryButton={
          // SH-1525 -- history is still viewable even with no active status
          // (e.g. it was deleted from Settings), so gate on real history
          // existing at all, not on there being more than one entry.
          hasViewableHistory(
            knockStatus?.knockStatusCount ?? 0,
            knockStatus?.hasActiveStatus ?? false,
          ) ? (
            <Button
              variant="text"
              size="small"
              onClick={handleViewKnockStatusHistoryClick}
              sx={{ color: '#1565C0' }}
            >
              View Knock Status History
            </Button>
          ) : null
        }
        canEdit={canEdit && hasViewPermission}
        isSavingReason={isSavingReason}
        onSaveReason={handleSaveReason}
      />

      {!noViewPermissionForAllLocations && (
        <Box mt={1.6}>
          {hasViewPermission ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                gap: 0.8,
              }}
            >
              {isD2dEnabled ? (
                <Button
                  fullWidth
                  variant="contained"
                  sx={{
                    backgroundColor: '#D4E3F3',
                  }}
                  onClick={handleKnockStatusModalOpen}
                  color="secondary"
                >
                  Knock Status
                </Button>
              ) : null}
              {onViewMoreClick ? (
                <Button fullWidth variant="contained" onClick={onViewMoreClick}>
                  View Full Details
                </Button>
              ) : null}
            </Box>
          ) : (
            <Alert severity="warning">
              <Typography variant="body2">
                You don&apos;t have permission to view this record.
              </Typography>
            </Alert>
          )}
        </Box>
      )}

      {openKnockStatusModal && (
        <KnockStatusModal
          open={openKnockStatusModal}
          onClose={handleKnockStatusModalClose}
          onDeleteSuccess={handleKnockStatusModalClose}
          onRefresh={onRefresh}
        />
      )}

      <ClearKnockStatusConfirmDialog
        open={clearConfirmOpen}
        isClearing={isClearing}
        onCancel={closeClearConfirm}
        onConfirm={handleClearKnockStatus}
      />
    </Stack>
  )
}

export default MultiTenantDrawerContentSingle
